/**
 * IT-focused roll-up for config drift on Diagnostics: overall status, data source,
 * per-type triage, activation lag vs deploy, and copy-for-ticket text.
 */

import React, { useMemo, useCallback } from 'react';
import { Box, Typography, Chip, Alert, Button, Tooltip } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import type { ConfigDriftResponse, TypeDrift, ConfigStatus } from '../services/configDrift';

function utcLabel(iso: string | undefined | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' Z');
}

function statusChipColor(
  s: ConfigStatus
): 'success' | 'error' | 'warning' | 'default' {
  switch (s) {
    case 'converged':
      return 'success';
    case 'drifted':
      return 'error';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
}

function appliedNotActivated(t: TypeDrift): boolean {
  const a = t.applied;
  const c = t.activated;
  return Boolean(a && c && (a.bundle_hash || '') !== (c.bundle_hash || ''));
}

/** Must match Diagnostics `handleConvergeDrift` republish targets (+ zones push). */
const CONFIG_TYPES_CONVERGE_FIXES = new Set(['policies', 'zones', 'drb', 'fcp']);

export interface ConfigDriftItSummaryProps {
  data: ConfigDriftResponse;
  deviceId: string;
}

export const ConfigDriftItSummary: React.FC<ConfigDriftItSummaryProps> = ({ data, deviceId }) => {
  const { types, overall_status, edge_merge, computed_at, stale_threshold_minutes } = data;

  const grouped = useMemo(() => {
    const drifted: string[] = [];
    const pending: string[] = [];
    const unknown: string[] = [];
    const activationLagConverged: string[] = [];
    const staleDrifted: string[] = [];

    for (const [key, t] of Object.entries(types || {})) {
      const td = t as TypeDrift;
      switch (td.status) {
        case 'drifted':
          drifted.push(key);
          break;
        case 'pending':
          pending.push(key);
          break;
        case 'converged':
          break;
        default:
          unknown.push(key);
      }
      if (td.status === 'converged' && appliedNotActivated(td)) {
        activationLagConverged.push(key);
      }
      if (
        td.status === 'drifted' &&
        td.stale_minutes != null &&
        td.stale_minutes > stale_threshold_minutes
      ) {
        staleDrifted.push(key);
      }
    }

    return {
      drifted,
      pending,
      unknown,
      activationLagConverged,
      staleDrifted,
    };
  }, [types, stale_threshold_minutes]);

  const edgeLine = useMemo(() => {
    if (edge_merge?.attempted !== true) {
      return 'Edge merge not attempted for this request.';
    }
    if (edge_merge.succeeded === true) {
      return 'Live edge health merged into this view (on-device columns reflect the device when reachable).';
    }
    return 'Edge unreachable or timed out — applied state is from cloud DB acknowledgements only until the device is reachable.';
  }, [edge_merge]);

  const ticketText = useMemo(() => {
    const lines: string[] = [
      `Config drift snapshot`,
      `device_id=${deviceId}`,
      `computed_at_utc=${utcLabel(computed_at)}`,
      `overall_status=${overall_status}`,
      `edge_merge_attempted=${edge_merge?.attempted ?? false} succeeded=${edge_merge?.succeeded ?? false}`,
      '',
    ];
    if (grouped.drifted.length) lines.push(`drifted: ${grouped.drifted.join(', ')}`);
    if (grouped.pending.length) lines.push(`pending: ${grouped.pending.join(', ')}`);
    if (grouped.unknown.length) lines.push(`unknown: ${grouped.unknown.join(', ')}`);
    if (grouped.activationLagConverged.length) {
      lines.push(
        `deploy_converged_but_live_engine_lagging: ${grouped.activationLagConverged.join(', ')}`
      );
    }
    if (grouped.staleDrifted.length) {
      lines.push(`stale_apply_while_drifted (>${stale_threshold_minutes}m): ${grouped.staleDrifted.join(', ')}`);
    }
    lines.push('');
    for (const [ct, t] of Object.entries(types || {})) {
      const td = t as TypeDrift;
      const d = td.desired;
      const ap = td.applied;
      const ac = td.activated;
      lines.push(`[${ct}] status=${td.status} reason=${td.reason_code ?? '—'}`);
      if (d) lines.push(`  desired hash=${d.bundle_hash} v=${d.bundle_version}`);
      if (ap) {
        lines.push(
          `  applied hash=${ap.bundle_hash} v=${ap.bundle_version} source=${ap.source} at_utc=${utcLabel(ap.applied_at)}`
        );
      }
      if (ac) {
        lines.push(
          `  activated hash=${ac.bundle_hash} v=${ac.bundle_version} at_utc=${utcLabel(ac.activated_at)}`
        );
      }
    }
    return lines.join('\n');
  }, [
    deviceId,
    computed_at,
    overall_status,
    edge_merge,
    grouped,
    types,
    stale_threshold_minutes,
  ]);

  const copySummary = useCallback(() => {
    navigator.clipboard?.writeText(ticketText).catch(() => {});
  }, [ticketText]);

  const showActivationInfo =
    grouped.activationLagConverged.length > 0 && grouped.drifted.length === 0 && grouped.pending.length === 0;

  const driftedOrPending = useMemo(
    () => new Set([...grouped.drifted, ...grouped.pending]),
    [grouped.drifted, grouped.pending]
  );
  const convergeFixesThese = useMemo(
    () => Array.from(driftedOrPending).filter((t) => CONFIG_TYPES_CONVERGE_FIXES.has(t)),
    [driftedOrPending]
  );
  const convergeDoesNotFixThese = useMemo(
    () => Array.from(driftedOrPending).filter((t) => !CONFIG_TYPES_CONVERGE_FIXES.has(t)),
    [driftedOrPending]
  );

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            IT summary
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Overall
            </Typography>
            <Chip
              size="small"
              label={overall_status}
              color={statusChipColor(overall_status)}
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Computed (UTC)
            </Typography>
            <Typography variant="caption" component="span" fontFamily="monospace">
              {utcLabel(computed_at)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
            {edgeLine}
          </Typography>
        </Box>
        <Tooltip title="Copy plain-text summary for tickets / chat">
          <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={copySummary}>
            Copy for ticket
          </Button>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
        {grouped.drifted.length > 0 && (
          <Chip size="small" color="error" variant="outlined" label={`Drifted: ${grouped.drifted.join(', ')}`} />
        )}
        {grouped.pending.length > 0 && (
          <Chip size="small" color="warning" variant="outlined" label={`Pending: ${grouped.pending.join(', ')}`} />
        )}
        {grouped.unknown.length > 0 && (
          <Chip size="small" variant="outlined" label={`Unknown: ${grouped.unknown.join(', ')}`} />
        )}
        {grouped.drifted.length === 0 && grouped.pending.length === 0 && (
          <Chip size="small" variant="outlined" label="No types in drifted or pending (cloud ↔ deploy path)" />
        )}
        {grouped.activationLagConverged.length > 0 && (
          <Chip
            size="small"
            color="info"
            variant="outlined"
            label={`Live engine behind deploy: ${grouped.activationLagConverged.join(', ')}`}
          />
        )}
        {grouped.staleDrifted.length > 0 && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`Stale apply while drifted: ${grouped.staleDrifted.join(', ')}`}
          />
        )}
      </Box>

      {(grouped.drifted.length > 0 || grouped.pending.length > 0) && convergeFixesThese.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Cloud and on-device differ for <strong>{convergeFixesThese.join(', ')}</strong>. Use <strong>Converge</strong> to
          run desired-state sync, republish retained MQTT for policies / zones / <strong>drb</strong> / <strong>fcp</strong>{' '}
          (as needed), and push zones when applicable. Converge does not wait for the live rules engine to reload; expand a
          row for deploy vs live hashes if behavior lags. If republish does not clear drift, run a full deploy from rollout
          (new bundle version).
        </Alert>
      )}

      {(grouped.drifted.length > 0 || grouped.pending.length > 0) && convergeDoesNotFixThese.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Converge does not handle:</strong> {convergeDoesNotFixThese.join(', ')}. Use the normal deploy path for
          those config types. <strong>db_ack</strong> on a row means applied state came from the cloud database, not live
          edge health — check connectivity if you expect live verification.
        </Alert>
      )}

      {showActivationInfo && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Deploy path matches the cloud for:{' '}
          <strong>{grouped.activationLagConverged.join(', ')}</strong>. If live health still shows an older bundle, the
          rules engine is likely still reloading — wait and refresh, or escalate with the copied ticket text.
        </Alert>
      )}

      {grouped.drifted.length === 0 &&
        grouped.pending.length === 0 &&
        grouped.unknown.length > 0 &&
        grouped.activationLagConverged.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Some config types are unknown (often nothing deployed yet). See per-row reason codes in the table or ticket
            copy.
          </Alert>
        )}
    </Box>
  );
};
